/**
 * Feature flags de l'interface — WO-V5-A / US-029.1 (jalon J5-3).
 *
 * v0.5.0 (U6a, B-602) — DÉCISION : le drapeau `navContexte` ne gouverne PLUS l'accueil.
 * L'accueil par profil (`AccueilProfil`) est la page d'arrivée de TOUS, en production
 * comprise, sans drapeau : c'est la navigation cible acceptée (porte D1, DEC-021), testée
 * (vitest et parcours Playwright), et l'ancien tableau de bord reste atteignable à
 * `/tableau-de-bord`.
 *
 * v0.5.0 (U6b, vague 1) — DÉCISION : le drapeau `navContexte` ne gouverne PLUS RIEN dans la
 * coquille. Le sélecteur de contexte (`BarreContexte`) est PERMANENT (§ 4.8 du canevas validé
 * le 2026-09-27). Le mécanisme de drapeaux reste, sans consommateur pour ce drapeau ; la
 * politique de build de `scripts/doit/build_images.py` est sans effet sur l'interface.
 *
 * v10 (paquet v0-p00-ouverture, décision D6 « par organisation ») — drapeau `navigationV10` : la coquille v10, la
 * connexion v10, les écrans publics et les routes des fragments `src/v10/routes/<zone>.tsx` ne sont rendus que s'il est
 * actif. Il a SON PROPRE modèle, gouverné par la POLITIQUE DE BUILD `VITE_FEATURE_NAVIGATION_V10` (revue du 27/10) :
 *  - absente, `off`/`false`/`0`, ou toute valeur inconnue → `eteint` : arrêt global, rien ne l'allume (images actuelles,
 *    déploiement de V0 en ombre). Une faute de frappe ferme, elle n'ouvre jamais (leçon FND-CR5-02).
 *  - `permis` → l'ORGANISATION décide : la valeur est posée en mémoire par le POINT D'AMORÇAGE
 *    (`definirValeurOrganisation`, appelé par l'extension `amorce` d'un fragment via `AmorceNavigationV10`, qu'App.tsx
 *    rend dès qu'une session existe, DRAPEAU ÉTEINT compris) ; sans valeur (avant la connexion, avant la réponse de la
 *    passerelle, après un échec de l'amorce) → éteint. La valeur appartient à l'identité qui l'a posée
 *    (`identiteSession` : utilisateur et organisation) ; lue pour une autre identité, elle ne compte pas, dès le rendu.
 *    Cible de J1 et des jalons suivants sur la pile vivante.
 *  - `on`/`true`/`1` → `tous` : allumé pour tous, verrouillé ; piles jetables et parcours E2E des vagues seulement.
 * AUCUNE surcharge locale (`localStorage`) pour ce drapeau : un navigateur ne peut jamais l'allumer.
 *
 * `navContexte` garde le modèle historique ci-dessous (build verrouillé > local > défaut), inchangé.
 * UN SEUL MÉCANISME, QUATRE SOURCES, DANS CET ORDRE DE PRIORITÉ (navContexte) :
 *  1. `build`  — variable de build du flag (`VITE_FEATURE_NAV_CONTEXTE`) lue par Vite AU BUILD (`on`/`off`) :
 *                toute valeur reconnue VERROUILLE le flag.
 *  2. `organisation` — sans effet sur `navContexte` (source réservée à `navigationV10`).
 *  3. `local`  — surcharge `localStorage` (clé du flag, `on`/`off`), honorée UNIQUEMENT quand le build n'a pas fixé
 *                de valeur (dev, démo, pile jetable).
 *                Elle est TRACÉE : `console.info` à l'écriture, et `data-flag-nav-contexte=
 *                "on:local"` sur le shell — jamais un comportement caché.
 *  4. `defaut` — OFF.
 *
 * Défaut OFF = l'interface actuelle strictement inchangée (critère 4 de la carte). Aucun
 * appel réseau, aucune dépendance : lisible dès le premier rendu, sans état intermédiaire.
 */
import { useSyncExternalStore } from 'react'

export type FlagId = 'navContexte' | 'navigationV10'
export type FlagSource = 'build' | 'organisation' | 'local' | 'defaut'
export type ValeurFlag = 'on' | 'off'

export interface EtatFlag {
  actif: boolean
  source: FlagSource
  /** Vrai quand aucune surcharge locale ne peut changer la valeur (build fixé ; toujours vrai pour navigationV10). */
  verrouille: boolean
}

/** Drapeaux qu'une surcharge locale peut changer : `navigationV10` n'en fait pas partie (D6, revue du 27/10). */
export type FlagSurchargeable = 'navContexte'

/** Clés `localStorage` de surcharge, par flag — un contrat stable pour les tests navigateur. */
export const CLES_STOCKAGE_FLAGS: Record<FlagSurchargeable, string> = {
  navContexte: 'doit-flag-nav-contexte',
}

/** Politique de build de `navigationV10` : `eteint` (défaut, arrêt global), `permis` (l'organisation décide), `tous`. */
export type PolitiqueNavigationV10 = 'eteint' | 'permis' | 'tous'

type EnvFlags = Partial<Record<keyof ImportMetaEnv, string | undefined>>

/** Lit `VITE_FEATURE_NAVIGATION_V10` ; toute valeur absente ou inconnue vaut `eteint`. */
export function politiqueNavigationV10(env: EnvFlags = import.meta.env): PolitiqueNavigationV10 {
  const brut = env.VITE_FEATURE_NAVIGATION_V10
  if (typeof brut === 'string' && brut.trim().toLowerCase() === 'permis') return 'permis'
  return normaliserValeurFlag(brut) === 'on' ? 'tous' : 'eteint'
}

/** Variable de build des drapeaux à modèle historique ; `navigationV10` a sa politique (`politiqueNavigationV10`). */
const VARIABLES_BUILD: Record<FlagSurchargeable, keyof ImportMetaEnv> = {
  navContexte: 'VITE_FEATURE_NAV_CONTEXTE',
}

/** Événement DOM émis à chaque écriture de surcharge : les hooks se ré-abonnent dessus. */
export const EVENEMENT_FLAGS = 'doit-flags-change'

/** Valeur d'organisation de `navigationV10` et son PROPRIÉTAIRE (`identiteSession`) : elle ne vaut que pour lui. */
let VALEUR_ORGANISATION: { valeur: ValeurFlag; identite: string } | null = null

/**
 * Identité à laquelle appartient une valeur d'organisation : utilisateur et organisation de la session. Un changement
 * d'utilisateur ou d'organisation rend aussitôt l'ancienne valeur sans effet, dès le rendu (revue Droid du 27/10).
 */
export function identiteSession(session: { userId?: string; username: string; organisationId?: string } | null): string | null {
  return session ? `${session.userId ?? session.username}|${session.organisationId ?? ''}` : null
}

/**
 * Valeur de l'organisation courante, posée par le point d'amorçage (`AmorceNavigationV10`, rendu drapeau éteint) et
 * effacée (`null`) à son démontage. Sans effet sauf politique `permis`.
 */
export function definirValeurOrganisation(id: 'navigationV10', valeur: ValeurFlag | null, identite: string): void {
  if (valeur !== null) VALEUR_ORGANISATION = { valeur, identite }
  else if (VALEUR_ORGANISATION?.identite === identite) VALEUR_ORGANISATION = null
  else return
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVENEMENT_FLAGS, { detail: { flag: id, valeur } }))
}

/** `on`/`true`/`1` → `on` ; `off`/`false`/`0` → `off` ; tout le reste → `null` (ignoré). */
export function normaliserValeurFlag(valeur: string | null | undefined): ValeurFlag | null {
  if (valeur == null) return null
  const v = valeur.trim().toLowerCase()
  if (v === 'on' || v === 'true' || v === '1') return 'on'
  if (v === 'off' || v === 'false' || v === '0') return 'off'
  return null
}

function stockageParDefaut(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function lireStockage(storage: Storage | null, cle: string): string | null {
  if (!storage) return null
  try {
    return storage.getItem(cle)
  } catch {
    // Navigation privée / quota : fonctionnement nominal sans surcharge.
    return null
  }
}

export function lireFlag(
  id: FlagId,
  options: {
    env?: EnvFlags; storage?: Storage | null
    /** Tests : valeur d'organisation imposée (`null` = aucune) ; absente, la valeur mémorisée pour `identite`. */
    organisation?: ValeurFlag | null
    /** Identité de la session courante (`identiteSession`) ; absente ou nulle, aucune valeur d'organisation ne compte. */
    identite?: string | null
  } = {},
): EtatFlag {
  const env = options.env ?? import.meta.env
  if (id === 'navigationV10') {
    const politique = politiqueNavigationV10(env)
    if (politique === 'tous') return { actif: true, source: 'build', verrouille: true }
    if (politique === 'permis') {
      const memorisee = options.identite != null && VALEUR_ORGANISATION?.identite === options.identite
        ? VALEUR_ORGANISATION.valeur : null
      const organisation = options.organisation === undefined ? memorisee : options.organisation
      if (organisation != null) return { actif: organisation === 'on', source: 'organisation', verrouille: true }
      return { actif: false, source: 'defaut', verrouille: true }
    }
    const brut = env.VITE_FEATURE_NAVIGATION_V10
    return { actif: false, source: brut == null || brut.trim() === '' ? 'defaut' : 'build', verrouille: true }
  }
  const storage = options.storage === undefined ? stockageParDefaut() : options.storage
  const build = normaliserValeurFlag(env[VARIABLES_BUILD[id]])
  if (build !== null) return { actif: build === 'on', source: 'build', verrouille: true }
  const local = normaliserValeurFlag(lireStockage(storage, CLES_STOCKAGE_FLAGS[id]))
  if (local !== null) return { actif: local === 'on', source: 'local', verrouille: false }
  return { actif: false, source: 'defaut', verrouille: false }
}

/** Écrit (ou efface avec `null`) la surcharge locale, la trace, et notifie les abonnés. */
export function ecrireSurchargeLocale(id: FlagSurchargeable, valeur: ValeurFlag | null, storage: Storage | null = stockageParDefaut()): void {
  const cle = CLES_STOCKAGE_FLAGS[id]
  try {
    if (valeur) storage?.setItem(cle, valeur)
    else storage?.removeItem(cle)
  } catch {
    /* stockage indisponible : rien à persister, on notifie quand même */
  }
  console.info(`[flags] ${id} : surcharge locale = ${valeur ?? 'aucune'} (dev/démo uniquement ; un build avec ${VARIABLES_BUILD[id]} la verrouille)`, { flag: id, valeur })
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVENEMENT_FLAGS, { detail: { flag: id, valeur } }))
}

function abonner(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(EVENEMENT_FLAGS, callback)
  window.addEventListener('storage', callback)
  return () => {
    window.removeEventListener(EVENEMENT_FLAGS, callback)
    window.removeEventListener('storage', callback)
  }
}

/** Instantané sérialisé : `useSyncExternalStore` compare par égalité stricte. */
function instantane(id: FlagId, identite: string | null): string {
  const e = lireFlag(id, { identite })
  return `${e.actif ? 'on' : 'off'}:${e.source}:${e.verrouille ? '1' : '0'}`
}

/**
 * Lecture réactive d'un flag : se met à jour quand la surcharge locale ou la valeur d'organisation change. `identite`
 * (`identiteSession(session)`) : seule une valeur d'organisation posée pour cette identité compte (`navigationV10`).
 */
export function useFeatureFlag(id: FlagId, identite: string | null = null): EtatFlag {
  const brut = useSyncExternalStore(abonner, () => instantane(id, identite), () => instantane(id, identite))
  const [actif, source, verrouille] = brut.split(':') as [string, FlagSource, string]
  return { actif: actif === 'on', source, verrouille: verrouille === '1' }
}

/** Valeur DOM tracée sur le shell : `on:local`, `on:build`, `off:build`… — lisible par les tests. */
export function traceFlag(etat: EtatFlag): string {
  return `${etat.actif ? 'on' : 'off'}:${etat.source}`
}
